import { Box, TextField } from '@mui/material'
import InvoiceList from '../invoice-list/invoice-list'
import { useListInvoicesByUserQuery } from '../../../../store/invoices/invoices.api'
import { ListInvoiceParams } from '../../../../store/invoices/invoices.types'
import InvoiceDaysButtons from 'components/invoice-days-buttons/invoice-days-buttons.component'
import { useEffect, useMemo, useState } from 'react'
import { useGetUserCompanyAssociationByUserQuery } from 'store/company/company.api'
import { getUserId } from 'utils/common'
import {
  factoringCompanyIdSet,
  filterTransformInvoiceGroups,
  filterTransformInvoiceGroupsByPodSubTab,
} from 'pages/invoice/invoice.utils'
import { isToBeInvoicedWorkspaceTab } from 'store/invoices/invoices.types'
import { usePodPresenceForOrders } from 'pages/invoice/use-pod-presence-for-orders'
import { useInvoiceWorkspaceScope } from 'pages/invoice/invoice-workspace.context'

const InvoiceListContainer = ({ invoiceType }: ListInvoiceParams) => {
  const customerScope = useInvoiceWorkspaceScope()
  const userId = getUserId()
  const { data: associations } = useGetUserCompanyAssociationByUserQuery(
    { userId },
    { skip: !userId }
  )
  const factoringIds = useMemo(
    () => (associations !== undefined ? factoringCompanyIdSet(associations) : undefined),
    [associations]
  )

  const { data: invoices } = useListInvoicesByUserQuery({
    invoiceType,
  })

  const [search, setSearch] = useState('')

  useEffect(() => {
    setSearch('')
  }, [invoiceType, customerScope])

  const scopedInvoices = useMemo(
    () => filterTransformInvoiceGroups(invoices, customerScope, factoringIds),
    [invoices, customerScope, factoringIds]
  )

  const orderIdsForPod = useMemo(() => {
    if (!isToBeInvoicedWorkspaceTab(invoiceType)) return []
    return scopedInvoices.flatMap((g) =>
      g.invoiceData.map((inv) => inv.orderId).filter((id): id is string => Boolean(id))
    )
  }, [invoiceType, scopedInvoices])

  const podByOrderId = usePodPresenceForOrders(orderIdsForPod)

  const tabScopedInvoices = useMemo(
    () =>
      filterTransformInvoiceGroupsByPodSubTab(
        scopedInvoices,
        invoiceType,
        podByOrderId
      ),
    [scopedInvoices, invoiceType, podByOrderId]
  )

  const filteredInvoices = useMemo(() => {
    if (!tabScopedInvoices.length) return []

    const query = search.trim().toLowerCase()
    const matched = query
      ? tabScopedInvoices.filter((invoice) =>
          (invoice.customerName || '').toLowerCase().includes(query)
        )
      : tabScopedInvoices

    return [...matched].sort((a, b) =>
      (a.customerName || '').localeCompare(b.customerName || '', undefined, {
        sensitivity: 'base',
      })
    )
  }, [tabScopedInvoices, search])

  return (
    <Box mt={2}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 8,
          bgcolor: 'background.paper',
          pb: 1,
        }}
      >
        {invoiceType === 'NOT_PAID' && <InvoiceDaysButtons />}
        <TextField
          type="text"
          placeholder="Search Customer"
          variant="outlined"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ width: '100%', marginTop: invoiceType === 'NOT_PAID' ? 2 : 0 }}
        />
      </Box>
      {filteredInvoices.length > 0 && <InvoiceList invoices={filteredInvoices} />}
    </Box>
  )
}

export default InvoiceListContainer
